// Agendas par service : plages hebdomadaires, durée des créneaux, capacité par créneau,
// fermetures, délai d'annulation ; création d'un agenda (droit de modifier les paramètres).
import type { Agenda } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Dialogue, EtatVide, GrilleChamps, LienBouton, Panneau, Selection, ZoneTexte } from '@cc/ui';
import { ArrowLeft, CalendarDays, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { requete } from '../../api/client';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date as formaterDate } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { moduleActif, peut } from '../../session/permissions';
import { useServices } from '../demarches/contenu';
import { JOURS, useAgendas } from './donnees';
import commun from '../../commun/commun.module.css';
import styles from './rendez-vous.module.css';

type Plage = Agenda['plages'][number];
type Fermeture = Agenda['fermetures'][number];

const VIDE = { service_id: '', libelle: '', lieu: '', description: '', duree_creneau_min: 30, capacite: 1, delai_annulation_heures: 24 };

function FormulaireAgenda({ ouvert, onFermer }: { ouvert: boolean; onFermer: () => void }) {
  const services = useServices();
  const [s, setS] = useState(VIDE);
  const [plages, setPlages] = useState<Plage[]>([{ jour: 1, debut: '08:00', fin: '12:00' }]);
  const [fermetures, setFermetures] = useState<Fermeture[]>([]);
  const creation = useAction({
    executer: () => requete<Agenda>('/agendas', { methode: 'POST', corps: { ...s, lieu: s.lieu || undefined, description: s.description || undefined, plages, fermetures } }),
    succes: (a) => `Agenda « ${a.libelle} » créé.`,
    invalider: [['agendas']],
    apres: () => {
      onFermer();
      setS(VIDE);
      setPlages([{ jour: 1, debut: '08:00', fin: '12:00' }]);
      setFermetures([]);
    },
  });
  const erreur = (champ: string) => creation.erreursChamps.find((c) => c.champ === champ || c.champ.startsWith(`${champ}.`))?.message;
  const nombre = (cle: 'duree_creneau_min' | 'capacite' | 'delai_annulation_heures') => (e: { target: { value: string } }) => setS((x) => ({ ...x, [cle]: Number(e.target.value) || 0 }));
  return (
    <Dialogue
      ouvert={ouvert}
      titre="Nouvel agenda"
      onFermer={onFermer}
      actions={
        <>
          <Bouton variante="secondaire" onClick={onFermer}>
            Annuler
          </Bouton>
          <Bouton chargement={creation.enCours} disabled={!s.service_id || s.libelle.trim().length < 2 || plages.length === 0} onClick={() => creation.lancer(undefined)}>
            Créer l&apos;agenda
          </Bouton>
        </>
      }
    >
      <GrilleChamps>
        <Selection id="agenda-service" libelle="Service" value={s.service_id} required optionVide="Choisir un service" options={(services.data ?? []).map((x) => ({ valeur: x.id, libelle: x.nom }))} erreur={erreur('service_id')} onChange={(e) => setS((x) => ({ ...x, service_id: e.target.value }))} />
        <Champ id="agenda-libelle" libelle="Libellé" value={s.libelle} required erreur={erreur('libelle')} onChange={(e) => setS((x) => ({ ...x, libelle: e.target.value }))} />
        <Champ id="agenda-lieu" libelle="Lieu" value={s.lieu} facultatif onChange={(e) => setS((x) => ({ ...x, lieu: e.target.value }))} />
      </GrilleChamps>
      <ZoneTexte id="agenda-description" libelle="Description affichée à l'usager" value={s.description} facultatif rows={2} onChange={(e) => setS((x) => ({ ...x, description: e.target.value }))} />
      <GrilleChamps>
        <Champ id="agenda-duree" libelle="Durée d'un créneau (minutes)" type="number" min={5} max={480} value={s.duree_creneau_min} required erreur={erreur('duree_creneau_min')} onChange={nombre('duree_creneau_min')} />
        <Champ id="agenda-capacite" libelle="Capacité par créneau" type="number" min={1} max={100} value={s.capacite} required erreur={erreur('capacite')} onChange={nombre('capacite')} />
        <Champ id="agenda-annulation" libelle="Délai d'annulation (heures)" type="number" min={0} max={720} value={s.delai_annulation_heures} required erreur={erreur('delai_annulation_heures')} onChange={nombre('delai_annulation_heures')} />
      </GrilleChamps>
      <fieldset className={styles.bloc}>
        <legend>Plages hebdomadaires</legend>
        {erreur('plages') ? <Bandeau type="erreur">{erreur('plages')}</Bandeau> : null}
        {plages.map((p, i) => (
          <div key={i} className={styles.lignePlage}>
            <Selection libelle={`Jour (plage ${i + 1})`} value={String(p.jour)} options={JOURS.map((j, k) => ({ valeur: String(k + 1), libelle: j }))} onChange={(e) => setPlages(plages.map((x, j) => (j === i ? { ...x, jour: Number(e.target.value) } : x)))} />
            <Champ libelle="Début" type="time" value={p.debut} onChange={(e) => setPlages(plages.map((x, j) => (j === i ? { ...x, debut: e.target.value } : x)))} />
            <Champ libelle="Fin" type="time" value={p.fin} onChange={(e) => setPlages(plages.map((x, j) => (j === i ? { ...x, fin: e.target.value } : x)))} />
            <Bouton petit variante="discret" icone={Trash2} aria-label={`Retirer la plage ${i + 1}`} disabled={plages.length <= 1} onClick={() => setPlages(plages.filter((_, j) => j !== i))}>
              Retirer
            </Bouton>
          </div>
        ))}
        <div>
          <Bouton petit variante="secondaire" icone={Plus} onClick={() => setPlages([...plages, { jour: Math.min(7, (plages.at(-1)?.jour ?? 0) + 1), debut: '08:00', fin: '12:00' }])}>
            Ajouter une plage
          </Bouton>
        </div>
      </fieldset>
      <fieldset className={styles.bloc}>
        <legend>Fermetures</legend>
        {fermetures.map((f, i) => (
          <div key={i} className={styles.lignePlage}>
            <Champ libelle={`Du (fermeture ${i + 1})`} type="date" value={f.debut} onChange={(e) => setFermetures(fermetures.map((x, j) => (j === i ? { ...x, debut: e.target.value } : x)))} />
            <Champ libelle="Au" type="date" value={f.fin} onChange={(e) => setFermetures(fermetures.map((x, j) => (j === i ? { ...x, fin: e.target.value } : x)))} />
            <Champ libelle="Motif" value={f.motif} onChange={(e) => setFermetures(fermetures.map((x, j) => (j === i ? { ...x, motif: e.target.value } : x)))} />
            <Bouton petit variante="discret" icone={Trash2} aria-label={`Retirer la fermeture ${i + 1}`} onClick={() => setFermetures(fermetures.filter((_, j) => j !== i))}>
              Retirer
            </Bouton>
          </div>
        ))}
        <div>
          <Bouton petit variante="secondaire" icone={Plus} onClick={() => setFermetures([...fermetures, { debut: '', fin: '', motif: 'Fermeture' }])}>
            Ajouter une fermeture
          </Bouton>
        </div>
      </fieldset>
      {creation.messageErreur ? <Bandeau type="erreur">{creation.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

export function PageAgendas() {
  const { contexte } = useSession();
  const agendas = useAgendas();
  const services = useServices();
  const [creation, setCreation] = useState(false);
  const nomService = (id: string) => services.data?.find((s) => s.id === id)?.nom ?? 'Service';
  return (
    <>
      <EnTetePage
        etiquette="Rendez-vous"
        titre="Agendas des services"
        description="Chaque agenda définit les plages d'accueil, la durée des créneaux, la capacité par créneau (places numérotées, imposée par la base) et les fermetures. Les usagers réservent dans le portail."
        actions={
          <>
            <LienBouton href="/rendez-vous" icone={ArrowLeft}>
              Rendez-vous du jour
            </LienBouton>
            {peut(contexte, 'parametres', 'modifier') && moduleActif(contexte, 'M05') !== false ? (
              <Bouton variante="principal" icone={Plus} onClick={() => setCreation(true)}>
                Nouvel agenda
              </Bouton>
            ) : null}
          </>
        }
      />
      <EtatRequete chargement={agendas.isPending} erreur={agendas.error} onReessayer={() => void agendas.refetch()} lot="L1-A" fonction="Les agendas">
        {(agendas.data ?? []).length === 0 ? (
          <EtatVide titre="Aucun agenda" icone={CalendarDays}>
            <p>Aucun service ne propose encore de rendez-vous.</p>
          </EtatVide>
        ) : (
          <div className={styles.agendas}>
            {(agendas.data ?? []).map((a) => (
              <Panneau key={a.id} titre={a.libelle} niveau={3}>
                <dl className={commun.definitions}>
                  <dt>Service</dt>
                  <dd>{nomService(a.service_id)}</dd>
                  <dt>Lieu</dt>
                  <dd>{a.lieu ?? 'non renseigné'}</dd>
                  <dt>Créneaux</dt>
                  <dd>
                    {a.duree_creneau_min} minutes, {a.capacite} place{a.capacite > 1 ? 's' : ''} par créneau, annulation jusqu&apos;à {a.delai_annulation_heures} h avant
                  </dd>
                  <dt>Plages</dt>
                  <dd>{a.plages.map((p) => `${JOURS[p.jour - 1]} ${p.debut} à ${p.fin}`).join(' ; ')}</dd>
                  <dt>Fermetures</dt>
                  <dd>{a.fermetures.length === 0 ? 'aucune' : a.fermetures.map((f) => `du ${formaterDate(f.debut)} au ${formaterDate(f.fin)} (${f.motif})`).join(' ; ')}</dd>
                </dl>
                <LienBouton href={`/rendez-vous/semaine?agenda=${a.id}`} petit>
                  Voir la semaine de cet agenda
                </LienBouton>
              </Panneau>
            ))}
          </div>
        )}
      </EtatRequete>
      <FormulaireAgenda ouvert={creation} onFermer={() => setCreation(false)} />
    </>
  );
}
